import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Grid,
  Paper,
  Divider,
  Alert,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CancelIcon from '@mui/icons-material/Cancel';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import StoreIcon from '@mui/icons-material/Store';
import HistoryIcon from '@mui/icons-material/History';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import axiosInstance from '../../lib/axios';
import { useSnackbar } from '../../components/SnackbarProvider';
import { palette } from '../../theme/colors';
import type { StockTransfer, Location } from '../../types';

const fetchStockTransfer = async (id: string): Promise<StockTransfer> => {
  const response = await axiosInstance.get(`/stock-transfers/${id}`);
  return response.data;
};

const fetchLocations = async (): Promise<Location[]> => {
  const response = await axiosInstance.get('/locations/admin/all');
  return Array.isArray(response.data) ? response.data : response.data.data || [];
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'pending':
      return 'warning';
    case 'approved':
      return 'info';
    case 'in_transit':
      return 'primary';
    case 'partially_completed':
      return 'info';
    case 'completed':
      return 'success';
    case 'cancelled':
      return 'error';
    default:
      return 'default';
  }
};

const getItemStatusColor = (status: string) => {
  switch (status) {
    case 'pending':
      return palette.warning;
    case 'shipped':
      return palette.primary;
    case 'received':
      return palette.success;
    case 'cancelled':
      return palette.error;
    default:
      return palette.textMuted;
  }
};

const getStatusLabel = (status: string) => {
  return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

export const StockTransferShow = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();

  const { data: transfer, isLoading } = useQuery({
    queryKey: ['stock-transfer', id],
    queryFn: () => fetchStockTransfer(id!),
    enabled: !!id,
  });

  const { data: locations = [] } = useQuery({
    queryKey: ['locations'],
    queryFn: fetchLocations,
  });

  const approveMutation = useMutation({
    mutationFn: () => axiosInstance.patch(`/stock-transfers/${id}/approve`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-transfer', id] });
      queryClient.invalidateQueries({ queryKey: ['stock-transfers'] });
      showMessage('Transfer approved successfully', 'success');
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to approve transfer', 'error');
    },
  });

  const shipMutation = useMutation({
    mutationFn: () => axiosInstance.patch(`/stock-transfers/${id}/ship`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-transfer', id] });
      queryClient.invalidateQueries({ queryKey: ['stock-transfers'] });
      showMessage('Transfer marked as shipped', 'success');
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to mark as shipped', 'error');
    },
  });

  const completeMutation = useMutation({
    mutationFn: () => axiosInstance.patch(`/stock-transfers/${id}/complete`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-transfer', id] });
      queryClient.invalidateQueries({ queryKey: ['stock-transfers'] });
      showMessage('Transfer completed successfully', 'success');
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to complete transfer', 'error');
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => axiosInstance.patch(`/stock-transfers/${id}/cancel`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-transfer', id] });
      queryClient.invalidateQueries({ queryKey: ['stock-transfers'] });
      showMessage('Transfer cancelled', 'success');
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to cancel transfer', 'error');
    },
  });

  if (isLoading || !transfer) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  const fromLocationName =
    locations.find((loc) => loc.id === transfer.fromLocationId)?.name ||
    transfer.fromLocationId;

  const toLocationName =
    locations.find((loc) => loc.id === transfer.toLocationId)?.name ||
    transfer.toLocationId;

  const canApprove = transfer.status === 'pending';
  const canShip = transfer.status === 'approved';
  const canComplete = transfer.status === 'in_transit';
  const canCancel = transfer.status === 'pending' || transfer.status === 'approved';

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/app/stock-transfers')}
          >
            Back
          </Button>
          <Typography variant="h4">Stock Transfer Details</Typography>
        </Box>
        <Chip
          label={getStatusLabel(transfer.status)}
          color={getStatusColor(transfer.status)}
          size="medium"
        />
      </Box>

      <Grid container spacing={3}>
        {/* Transfer Information */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Transfer Information
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                {/* Transfer Type Badge */}
                {transfer.isMultiProduct && (
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Inventory2Icon sx={{ color: palette.accent }} />
                      <Chip
                        label={`Multi-Product Transfer (${transfer.items?.length || 0} items)`}
                        sx={{ bgcolor: palette.accent, color: '#FFFFFF', fontWeight: 600 }}
                        size="small"
                      />
                    </Box>
                  </Grid>
                )}

                {/* Product or Item Count */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {transfer.isMultiProduct ? 'Products' : 'Product'}
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {transfer.isMultiProduct
                      ? `${transfer.items?.length || 0} different products`
                      : transfer.product?.name || 'Unknown'}
                  </Typography>
                </Grid>

                {/* Quantity */}
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Quantity
                  </Typography>
                  <Typography variant="h5" color="primary">
                    {transfer.isMultiProduct
                      ? transfer.items?.reduce((sum: number, item: any) => sum + (item.quantity || 0), 0) || 0
                      : transfer.quantity}
                  </Typography>
                </Grid>

                {/* Status */}
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={getStatusLabel(transfer.status)}
                    color={getStatusColor(transfer.status)}
                    size="small"
                  />
                </Grid>

                {/* Progress Bar for Multi-Product */}
                {transfer.isMultiProduct && transfer.status !== 'pending' && transfer.status !== 'cancelled' && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Transfer Progress
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={(() => {
                            const totalQty = transfer.items?.reduce((sum: number, item: any) => sum + (item.quantity || 0), 0) || 1;
                            const receivedQty = transfer.items?.reduce((sum: number, item: any) => sum + (item.quantityReceived || 0), 0) || 0;
                            return (receivedQty / totalQty) * 100;
                          })()}
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 60, textAlign: 'right' }}>
                        {(() => {
                          const totalQty = transfer.items?.reduce((sum: number, item: any) => sum + (item.quantity || 0), 0) || 0;
                          const receivedQty = transfer.items?.reduce((sum: number, item: any) => sum + (item.quantityReceived || 0), 0) || 0;
                          return `${receivedQty}/${totalQty}`;
                        })()}
                      </Typography>
                    </Box>
                  </Grid>
                )}

                <Grid item xs={12} sm={6}>
                  <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      {transfer.fromLocation?.type === 'warehouse' ? (
                        <WarehouseIcon color="primary" />
                      ) : (
                        <StoreIcon color="secondary" />
                      )}
                      <Typography variant="subtitle2" color="text.secondary">
                        From
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>
                      {fromLocationName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {transfer.fromLocation?.type === 'warehouse' ? 'Warehouse' : 'Store'}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      {transfer.toLocation?.type === 'warehouse' ? (
                        <WarehouseIcon color="primary" />
                      ) : (
                        <StoreIcon color="secondary" />
                      )}
                      <Typography variant="subtitle2" color="text.secondary">
                        To
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>
                      {toLocationName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {transfer.toLocation?.type === 'warehouse' ? 'Warehouse' : 'Store'}
                    </Typography>
                  </Paper>
                </Grid>

                {transfer.notes && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Notes
                    </Typography>
                    <Typography variant="body2">{transfer.notes}</Typography>
                  </Grid>
                )}

                {transfer.trackingNumber && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Tracking Number
                    </Typography>
                    <Typography variant="body1">{transfer.trackingNumber}</Typography>
                  </Grid>
                )}

                {transfer.carrier && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Carrier
                    </Typography>
                    <Typography variant="body1">{transfer.carrier}</Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* Item Breakdown for Multi-Product Transfers */}
          {transfer.isMultiProduct && transfer.items && transfer.items.length > 0 && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Items Breakdown
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>Quantity</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>Shipped</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>Received</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transfer.items.map((item: any, index: number) => (
                        <TableRow key={item.id || index}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {item.product?.name || 'Unknown Product'}
                            </Typography>
                            {item.notes && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {item.notes}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight={600}>
                              {item.quantity}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" color={item.quantityShipped > 0 ? 'primary' : 'text.secondary'}>
                              {item.quantityShipped || 0}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" color={item.quantityReceived > 0 ? 'success.main' : 'text.secondary'}>
                              {item.quantityReceived || 0}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={getStatusLabel(item.status)}
                              size="small"
                              sx={{
                                bgcolor: `${getItemStatusColor(item.status)}20`,
                                color: getItemStatusColor(item.status),
                                fontWeight: 600,
                                fontSize: '0.75rem',
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Timeline & Actions */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <HistoryIcon sx={{ color: palette.primary }} />
                <Typography variant="h6" fontWeight={600}>
                  Transfer Timeline
                </Typography>
              </Box>

              <Stack spacing={0}>
                {/* Requested */}
                {(() => {
                  const timelineEvents = [];

                  // Requested
                  timelineEvents.push({
                    label: 'Transfer Requested',
                    date: transfer.requestedAt,
                    user: transfer.requestedBy,
                    icon: <PlaylistAddCheckIcon fontSize="small" />,
                    color: palette.warning,
                  });

                  // Approved
                  if (transfer.approvedAt) {
                    timelineEvents.push({
                      label: 'Transfer Approved',
                      date: transfer.approvedAt,
                      user: transfer.approvedBy,
                      icon: <CheckCircleIcon fontSize="small" />,
                      color: palette.info,
                    });
                  }

                  // Shipped
                  if (transfer.shippedAt) {
                    timelineEvents.push({
                      label: 'Shipped',
                      date: transfer.shippedAt,
                      user: null,
                      icon: <LocalShippingIcon fontSize="small" />,
                      color: palette.primary,
                    });
                  }

                  // Completed
                  if (transfer.completedAt) {
                    timelineEvents.push({
                      label: 'Transfer Completed',
                      date: transfer.completedAt,
                      user: transfer.completedBy,
                      icon: <CheckCircleIcon fontSize="small" />,
                      color: palette.success,
                    });
                  }

                  // Cancelled
                  if (transfer.cancelledAt) {
                    timelineEvents.push({
                      label: 'Transfer Cancelled',
                      date: transfer.cancelledAt,
                      user: null,
                      icon: <CancelIcon fontSize="small" />,
                      color: palette.error,
                    });
                  }

                  return timelineEvents.map((event, index) => {
                    const isLast = index === timelineEvents.length - 1;

                    return (
                      <Box key={index} display="flex" gap={2}>
                        {/* Timeline line and dot */}
                        <Box display="flex" flexDirection="column" alignItems="center">
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: '50%',
                              bgcolor: `${event.color}20`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: event.color,
                            }}
                          >
                            {event.icon}
                          </Box>
                          {!isLast && (
                            <Box
                              sx={{
                                width: 2,
                                flex: 1,
                                minHeight: 40,
                                bgcolor: 'divider',
                              }}
                            />
                          )}
                        </Box>

                        {/* Content */}
                        <Box pb={isLast ? 0 : 3} flex={1}>
                          <Typography variant="body2" fontWeight={600}>
                            {event.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(event.date).toLocaleString()}
                          </Typography>
                          {event.user && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              by {event.user.firstName} {event.user.lastName}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  });
                })()}
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>
                Actions
              </Typography>

              <Box display="flex" flexDirection="column" gap={1}>
                {canApprove && (
                  <Button
                    fullWidth
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircleIcon />}
                    onClick={() => approveMutation.mutate()}
                    disabled={approveMutation.isPending}
                  >
                    Approve
                  </Button>
                )}

                {canShip && (
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    startIcon={<LocalShippingIcon />}
                    onClick={() => shipMutation.mutate()}
                    disabled={shipMutation.isPending}
                  >
                    Mark as Shipped
                  </Button>
                )}

                {canComplete && (
                  <Button
                    fullWidth
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircleIcon />}
                    onClick={() => completeMutation.mutate()}
                    disabled={completeMutation.isPending}
                  >
                    Complete Transfer
                  </Button>
                )}

                {canCancel && (
                  <Button
                    fullWidth
                    variant="outlined"
                    color="error"
                    startIcon={<CancelIcon />}
                    onClick={() => cancelMutation.mutate()}
                    disabled={cancelMutation.isPending}
                  >
                    Cancel Transfer
                  </Button>
                )}

                {!canApprove && !canShip && !canComplete && !canCancel && (
                  <Alert severity="info">
                    No actions available for this transfer in its current state.
                  </Alert>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
